import { PhoneOutlined, QuestionOutlined } from "@ant-design/icons";
import { Button, Select } from "antd";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import {
	getProfessorById,
	getStudentById,
} from "../Requests/academic-user-requests";
import { getProfilesByCursId, switchProfile } from "../Requests/auth-requests";
import {
	NOTIFICATION_TYPES,
	openNotification,
} from "../Components/Notifications/NotificationsUtils";
import { Routes } from "../utils/routes";
import {
	getUserId,
	isUserLogged,
	useIsCompany,
	useIsStudent,
} from "../utils/utilFunctions";
import { AcademicUserProfileDTO, Role } from "../Api";

const getProfileLabel = (
	profile: AcademicUserProfileDTO,
	t: (key: string) => string
): string => {
	const parts: (string | null | undefined)[] = [
		profile.role ? t(`header.roles.${profile.role}`) : undefined,
		profile.facultyAcronym,
	];
	if (profile.role === Role.Student && profile.year) {
		parts.push(profile.year);
	}
	return parts.filter(Boolean).join(" - ");
};

const langs = [
	{ value: "ro", label: "RO" },
	{ value: "en", label: "EN" },
];

const Navbar = () => {
	const { i18n, t } = useTranslation();
	const isCompany = useIsCompany();
	const isStudent = useIsStudent();
	const userId = getUserId();

	const { data: currentAcademicProfile } = useQuery(
		["currentAcademicProfile", userId, isStudent, isCompany],
		() => (isStudent ? getStudentById(userId) : getProfessorById(userId)),
		{ enabled: isUserLogged() && !isCompany && !!userId }
	);

	const cursId = currentAcademicProfile?.cursId ?? null;

	const { data: profilesResponse } = useQuery(
		["academicProfiles", cursId, isCompany],
		() => getProfilesByCursId(cursId!),
		{ enabled: isUserLogged() && !isCompany && !!cursId }
	);

	const profiles = profilesResponse?.profiles ?? [];

	const updateLang = (newLang: string) => i18n.changeLanguage(newLang);

	const handleAcademicProfileChange = async (selectedUserId: string) => {
		if (!selectedUserId || selectedUserId === userId) return;
		try {
			const response = await switchProfile(selectedUserId);
			localStorage.setItem("token", response.token);
			window.location.reload();
		} catch (error) {
			openNotification(
				t("header.switchProfileErrorTitle"),
				t("header.switchProfileErrorMessage"),
				NOTIFICATION_TYPES.ERROR
			);
		}
	};

	return (
		<div className="flex items-center gap-4">
			{profiles.length > 1 ? (
				<Select
					value={userId}
					onChange={handleAcademicProfileChange}
					className="min-w-[80px]"
				>
					{profiles
						.filter((profile) => !!profile.id)
						.map((profile) => (
							<Select.Option key={profile.id} value={profile.id}>
								{getProfileLabel(profile, t)}
							</Select.Option>
						))}
				</Select>
			) : profiles.length === 1 ? (
				<span className="text-sm">{getProfileLabel(profiles[0], t)}</span>
			) : null}

			<div className="hidden sm:flex items-center gap-4 ml-auto">
				<Button
					type="primary"
					shape="circle"
					icon={<QuestionOutlined />}
					size="middle"
					title={t("footer.frequentQuestions")}
					href={Routes.FAQ}
				/>

				<Button
					type="primary"
					shape="circle"
					icon={<PhoneOutlined />}
					size="middle"
					title={t("facultySettings.facultiesContactInformation")}
					href={Routes.FACULTIES_CONTACT}
				/>
			</div>

			<Select
				options={langs}
				defaultValue={i18n.resolvedLanguage ?? langs[0].value}
				onChange={updateLang}
				className="w-[64px]"
			/>
		</div>
	);
};

export default Navbar;
